import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFontSize } from '../contexts/FontSizeContext';
import { Menu, X, LogOut, User, LayoutDashboard, Type, ArrowLeft, ChevronDown } from 'lucide-react';
import NotificationBell from './NotificationBell';
import axios from 'axios';

const LOGO_URL = "/ioa_logo_dark.svg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Navbar() {
  const { user, logout, checkAuth } = useAuth();
  const { cycleSize, label } = useFontSize();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [learnOpen, setLearnOpen] = useState(false);

  // Active state helper — match exact path or path-with-children (e.g. /tutorials/foo also lights up Learn)
  const isActive = (paths) => {
    const arr = Array.isArray(paths) ? paths : [paths];
    return arr.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  };
  const tabClass = (paths) => `text-sm transition-colors duration-150 ${
    isActive(paths)
      ? 'text-[#F59E0B] font-bold'
      : 'text-zinc-300 hover:text-[#0077B6]'
  }`;

  const isTestAccount = user?.email?.endsWith('@ioausa.com');

  const backToAdmin = async () => {
    try {
      await axios.post(`${API}/admin/stop-impersonate`, {}, { withCredentials: true });
      await checkAuth();
      window.location.href = '/admin';
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin';
    if (user.role === 'employer') return '/employer';
    return '/dashboard';
  };

  return (
    <>
      {isTestAccount && (
        <div data-testid="impersonation-banner" className="bg-[#CC0000] text-white text-center py-2 px-4 flex items-center justify-center gap-3 text-sm font-medium">
          <span>Viewing as: <strong>{user?.name}</strong> ({user?.role})</span>
          <button onClick={backToAdmin} data-testid="back-to-admin" className="bg-white text-[#F59E0B] px-3 py-1 text-xs font-bold hover:bg-zinc-100 transition-colors flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Back to Admin
          </button>
        </div>
      )}
      <nav data-testid="navbar" className="sticky top-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-[#262626]">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-logo">
          <div className="h-10 overflow-hidden flex items-center">
            <img src={LOGO_URL} alt="Institute of Analytics USA" className="h-12 w-auto" />
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <div
            className="relative"
            onMouseEnter={() => setLearnOpen(true)}
            onMouseLeave={() => setLearnOpen(false)}
            data-testid="nav-learn-dropdown"
          >
            <button
              type="button"
              className={`${tabClass(['/courses', '/tutorials'])} flex items-center gap-1`}
              data-testid="nav-learn-trigger"
              onClick={() => setLearnOpen(o => !o)}
            >
              Learn <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
            {learnOpen && (
              <div className="absolute left-0 top-full pt-2 w-56 z-50">
                <div className="bg-[#111111] border border-[#262626] shadow-2xl py-2">
                  <Link
                    to="/courses"
                    className={`block px-4 py-2.5 text-sm transition-colors ${isActive('/courses') ? 'text-[#F59E0B] font-bold bg-[#F59E0B]/10' : 'text-zinc-200 hover:bg-[#0077B6]/10 hover:text-[#0077B6]'}`}
                    data-testid="nav-courses"
                    onClick={() => setLearnOpen(false)}
                  >
                    <div className="font-medium">Courses</div>
                    <div className="text-xs text-zinc-500 mt-0.5">PG Diploma — 6 courses</div>
                  </Link>
                  <Link
                    to="/tutorials"
                    className={`block px-4 py-2.5 text-sm transition-colors ${isActive('/tutorials') ? 'text-[#F59E0B] font-bold bg-[#F59E0B]/10' : 'text-zinc-200 hover:bg-[#0077B6]/10 hover:text-[#0077B6]'}`}
                    data-testid="nav-tutorials"
                    onClick={() => setLearnOpen(false)}
                  >
                    <div className="font-medium">Tutorials</div>
                    <div className="text-xs text-zinc-500 mt-0.5">Interactive hands-on lessons</div>
                  </Link>
                </div>
              </div>
            )}
          </div>
          <Link to="/pricing" className={tabClass('/pricing')} data-testid="nav-pricing">Pricing</Link>
          <Link to="/jobs" className={tabClass('/jobs')} data-testid="nav-jobs">Job Board</Link>
          <Link to="/career-advisor" className={tabClass('/career-advisor')} data-testid="nav-career-advisor">Resume Eval / Job Match</Link>
          <Link to="/blog" className={tabClass('/blog')} data-testid="nav-blog">Blog</Link>
          <Link to="/events" className={tabClass('/events')} data-testid="nav-events">Events</Link>
          <Link to="/partners" className={tabClass('/partners')} data-testid="nav-partners">Partners</Link>

          {/* Font Size Toggle */}
          <button
            onClick={cycleSize}
            data-testid="font-size-toggle"
            className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white border border-[#333] px-2.5 py-1 transition-colors"
            title="Increase text size for readability"
          >
            <Type className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="font-mono text-xs font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</span>
          </button>

          {user ? (
            <div className="flex items-center gap-4">
              <Link to={getDashboardLink()} className={`${tabClass(['/admin','/dashboard','/employer'])} flex items-center gap-1`} data-testid="nav-dashboard">
                <LayoutDashboard className="w-4 h-4" strokeWidth={1.5} />
                Dashboard
              </Link>
              <NotificationBell />
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <User className="w-4 h-4" strokeWidth={1.5} />
                <span data-testid="nav-user-name">{user.name}</span>
              </div>
              <button onClick={handleLogout} className="text-sm text-zinc-400 hover:text-[#F59E0B] transition-colors" data-testid="nav-logout">
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            // Combined gold pill: Sign In | Enroll Now
            <div className="inline-flex items-center rounded-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] p-0.5 shadow-[0_0_18px_rgba(245,158,11,0.35)] hover:shadow-[0_0_24px_rgba(245,158,11,0.55)] transition-shadow duration-200" data-testid="nav-auth-pill">
              <Link
                to="/login"
                className="px-5 py-1.5 text-sm font-bold text-[#0A0A0A] hover:bg-white/20 rounded-full transition-colors"
                data-testid="nav-login"
              >
                Sign In
              </Link>
              <span className="w-px h-5 bg-[#0A0A0A]/30" aria-hidden />
              <Link
                to="/register"
                className="px-5 py-1.5 text-sm font-bold text-[#0A0A0A] hover:bg-white/20 rounded-full transition-colors"
                data-testid="nav-register"
              >
                Enroll Now
              </Link>
            </div>
          )}
        </div>

        <button className="md:hidden text-white" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle">
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-[#262626] bg-[#0A0A0A] px-6 py-4 space-y-3">
          <Link to="/courses" className={`block text-base py-2 ${isActive('/courses') ? 'text-[#F59E0B] font-bold' : 'text-zinc-200'}`} onClick={() => setMobileOpen(false)}>Courses</Link>
          <Link to="/tutorials" className={`block text-base py-2 ${isActive('/tutorials') ? 'text-[#F59E0B] font-bold' : 'text-zinc-200'}`} onClick={() => setMobileOpen(false)} data-testid="nav-tutorials-mobile">Tutorials</Link>
          <Link to="/pricing" className={`block text-base py-2 ${isActive('/pricing') ? 'text-[#F59E0B] font-bold' : 'text-zinc-200'}`} onClick={() => setMobileOpen(false)}>Pricing</Link>
          <Link to="/jobs" className={`block text-base py-2 ${isActive('/jobs') ? 'text-[#F59E0B] font-bold' : 'text-zinc-200'}`} onClick={() => setMobileOpen(false)}>Job Board</Link>
          <Link to="/career-advisor" className={`block text-base py-2 ${isActive('/career-advisor') ? 'text-[#F59E0B] font-bold' : 'text-zinc-200'}`} onClick={() => setMobileOpen(false)}>Resume Eval / Job Match</Link>
          <Link to="/blog" className={`block text-base py-2 ${isActive('/blog') ? 'text-[#F59E0B] font-bold' : 'text-zinc-200'}`} onClick={() => setMobileOpen(false)}>Blog</Link>
          <Link to="/events" className={`block text-base py-2 ${isActive('/events') ? 'text-[#F59E0B] font-bold' : 'text-zinc-200'}`} onClick={() => setMobileOpen(false)}>Events</Link>
          <Link to="/partners" className={`block text-base py-2 ${isActive('/partners') ? 'text-[#F59E0B] font-bold' : 'text-zinc-200'}`} onClick={() => setMobileOpen(false)}>Partners</Link>
          <button onClick={() => { cycleSize(); }} className="block text-base text-zinc-400 py-2">Text Size: {label}</button>
          {user ? (
            <>
              <Link to={getDashboardLink()} className="block text-base text-zinc-200 py-2" onClick={() => setMobileOpen(false)}>Dashboard</Link>
              <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="text-base text-[#F59E0B] py-2">Logout</button>
            </>
          ) : (
            <div className="inline-flex items-center rounded-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] p-0.5 mt-2">
              <Link to="/login" className="px-5 py-1.5 text-sm font-bold text-[#0A0A0A] hover:bg-white/20 rounded-full transition-colors" onClick={() => setMobileOpen(false)}>Sign In</Link>
              <span className="w-px h-5 bg-[#0A0A0A]/30" aria-hidden />
              <Link to="/register" className="px-5 py-1.5 text-sm font-bold text-[#0A0A0A] hover:bg-white/20 rounded-full transition-colors" onClick={() => setMobileOpen(false)}>Enroll Now</Link>
            </div>
          )}
        </div>
      )}
    </nav>
    </>
  );
}
